import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { Alert, Card, CardContent, useTheme, Snackbar } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  SearchBasicTable,
  DataTableWidget,
  DialogState,
  IconActionTableState,
  IconActionTableEdit,
  BreadCrumbs,
  CardHeaderWidget,
  EditionAreaWidget,
  SnackBarsComponent,
  LabelChip,
} from "../../../components";
import { LoadingButton } from "@mui/lab";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { debounce } from "lodash";
import {
  concatObjectConfigDatatable,
  alterArrayGetFields,
  alphabeticalSort,
  handleActionEditNew,
} from "../../../helpers";

import { BsContainer, BsRow, BsCol } from "../../../layouts/components";
import PuEdit from "../PuEdit";
import viewsTexts from "../../../common/viewsTexts.json";
import snackBarsMessages from "../../../common/snackBarsMessages.json";
import { AxiosContext } from "../../../context/AxiosContext";
import { Context } from "../../../context/Context";
import { map } from "lodash";
import { useForm } from "react-hook-form";
import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";

const useStyles = makeStyles((theme) => ({
  tableContainerStyle: {
    [theme.breakpoints.up("sm")]: {
      paddingRight: 34,
    },
  },
  divider: {
    width: "1px",
    height: "28px",
    marginLeft: "20px",
    marginRight: "20px",
    backgroundColor: theme.palette.primary.main,
    opacity: 0.4,
  },
  actionColumn: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
}));
const snackBarMessage = snackBarsMessages.message["add"];

const PuList = () => {
  const theme = useTheme();
  const axiosContext = useContext(AxiosContext);
  const { userName } = useContext(Context);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const useClient = watch("clientValue");
  const useOrchard = watch("orchardValue");

  const [loadingDataTableState, setLoadingDataTableState] = useState(false);
  const [tableDataState, setTableDataState] = useState([]);
  const [openDialogStateState, setOpenDialogStateState] = useState(false);
  const [openDialogPollinatorState, setOpenDialogPollinatorState] =
    useState(false);
  const [limitState, setLimitState] = useState(10);
  const [sortState, setSortState] = useState({
    field: "client",
    order: "asc",
  });
  const [pageState, setPageState] = useState(1);
  const [countState, setCountState] = useState(0);
  const [editionState, setEditionState] = useState(false);
  const [rowToHandleState, setRowToHandleState] = useState("");
  const [rowToEditState, setRowToEditState] = useState("");
  const [clientDataState, setClientDataState] = useState([]);
  const [orchardDataState, setOrchardDataState] = useState([]);
  const [orchardFullDataState, setOrchardFullDataState] = useState([]);
  const [loadingDataState, setLoadingDataState] = useState(false);
  const [formQuarterState, setFormQuarterState] = useState("");

  const [clientSelected, setClientSelected] = useState(null);
  const [orchardSelected, setOrchardSelected] = useState(null);

  const [loadingStateState, setLoadingStateState] = useState(false);
  const [loadingStatePollinator, setLoadingStatePollinator] = useState(false);
  const [successStateState, setSuccessStateState] = useState(false);

  const [rowToHandleStatePollinator, setRowToHandleStatePollinator] =
    useState("");
  const [resetPagination, setResetPagination] = useState(false);
  const classes = useStyles();
  const customStyles = {
    headCells: {
      style: {
        color: "white",
        backgroundColor: theme.palette.primary.light,
        "&: nth-last-child(1)": {
          backgroundColor: theme.palette.primary.main,
          borderBottom: "4px solid",
          borderColor: theme.palette.secondary.light,
        },
        div: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      },
    },
  };

  const valuesToQuery = useMemo(() => {
    return {
      clientValue: useClient ? useClient.value : null,
      orchardValue: useOrchard ? useOrchard.value : null,
    };
  }, [useClient, useOrchard]);
  // Función para realizar la consulta de datos
  async function queryData(
    page = pageState,
    sort = sortState,
    searchTerm = formQuarterState,
    values = {}
  ) {
    setLoadingDataTableState(true);
    try {
      let objQuery = {
        clientValue: values.clientValue || null,
        orchardValue: values.orchardValue || null,
        search: searchTerm,
        page: page,
        limit: limitState,
        sortColumn: sort?.key || sortState.key,
        sortOrder: sort?.option || sortState.option,
      };

      const response = await axiosContext.authAxios.post(
        "/v1/unitProductive/list",
        objQuery
      );

      const { data, status } = response;

      if (status === 200) {
        setTableDataState(data.data);
        setCountState(data.count);
        setPageState(page);
        setSortState(sort);
        setOrchardSelected(values.orchardValue);
        setClientSelected(values.clientValue);
      }
    } catch (err) {
      setTableDataState([]);
    } finally {
      setLoadingDataTableState(false);
    }
  }

  // Función para manejar el cambio en el campo de búsqueda
  const handleSearchChange = (value) => {
    setFormQuarterState(value);
    setPageState(1);
    setResetPagination((prev) => !prev);
    debouncedSearch(value, 1);
  };

  // Función para realizar la búsqueda con debounce
  const debouncedSearch = useCallback(
    debounce((value, page = 1) => {
      queryData(page, undefined, value, valuesToQuery);
    }, 500),
    [useClient, useOrchard]
  );

  // Función para manejar el envío del formulario
  const onSubmit = async () => {
    setPageState(1);
    setResetPagination((prev) => !prev);
    queryData(1, sortState, undefined, valuesToQuery);
  };

  // Función para manejar el cambio de estado de un registro
  const DialogStateHandler = (row) => {
    setEditionState(false);
    setRowToHandleState(row);
    setOpenDialogStateState(!openDialogStateState);
  };

  // Función para confirmar el cambio de estado
  const stateActionDialogHandler = async (activeYears, newYears) => {
    setLoadingStateState(true);
    try {
      const bodyStatus = {
        idUnitProductive: rowToHandleState.idUnitProductive,
        username: userName,
        seasons: activeYears,
        newSeasons: newYears,
      };

      const responseSta = await axiosContext.authAxios.post(
        "v1/unitProductive/delete/",
        bodyStatus
      );

      if (responseSta.status === 200) {
        const seasonsResponse = responseSta.data;

        const newObj = {
          ...rowToHandleState,
          seasons: seasonsResponse?.seasons,
          statusUnitActive: seasonsResponse?.statusUnitActive,
        };
        setSuccessStateState(true);
        const newUsers = handleActionEditNew(newObj, tableDataState);
        setTableDataState(newUsers);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStateState(false);
    }
  };

  // Función para manejar el cambio de estado de los polinizantes
  const DialogStateHandlerPollinator = (row) => {
    setEditionState(false);
    setRowToHandleStatePollinator(row);
    setOpenDialogPollinatorState(!openDialogPollinatorState);
  };

  // Función para confirmar el cambio de estado del polinizante
  const stateActionDialogHandlerPollinator = async ({
    idUnitProductive,
    statusPollinator,
  }) => {
    setLoadingStatePollinator(true);
    try {
      const bodyStatus = {
        idUnitProductive,
        status: statusPollinator,
      };
      const responseSta = await axiosContext.authAxios.post(
        "v1/unitProductive/status-pollinator",
        bodyStatus
      );

      if (responseSta.status === 200) {
        const seasonsResponse = responseSta.data;
        const newObj = {
          ...rowToHandleStatePollinator,
          statusPollinator: seasonsResponse?.status,
        };
        setSuccessStateState(true);
        const newRow = handleActionEditNew(newObj, tableDataState);
        setTableDataState(newRow);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStatePollinator(false);
    }
  };
  const handleCloseSnackBarsComponent = (event, reason) => {
    setSuccessStateState(false);
  };
  // Función para cerrar el área de edición
  const closeEditionAreaHandler = () => {
    setEditionState(false);
  };

  // Función para manejar la edición de un registro
  const editActionHandler = (newObject) => {
    const newUsers = handleActionEditNew(newObject, tableDataState);
    setTableDataState(newUsers);
  };

  // Función para manejar el ordenamiento de columnas en la tabla
  const handleOnSort = async (column, sortDirection) => {
    let nameColumn = column.sortField;

    if (nameColumn !== null) {
      queryData(
        pageState,
        {
          key: nameColumn,
          option: sortDirection,
        },
        undefined,
        valuesToQuery
      );
    }
  };

  // Función para manejar el cambio de página en la tabla
  const handleChangePagination = (page) => {
    queryData(page, sortState, formQuarterState, {
      clientValue: clientSelected,
      orchardValue: orchardSelected,
    });
  };

  // Función para manejar el cambio de filas por página en la tabla
  const handleChangeRows = async (currentRowsPerPage, currentPage) => {
    try {
      let objQuery = {
        clientValue: useClient ? useClient.value : null,
        orchardValue: useOrchard ? useOrchard.value : null,
        page: currentPage,
        limit: currentRowsPerPage,
        sortColumn: sortState?.key,
        sortOrder: sortState?.option,
      };

      const response = await axiosContext.authAxios.post(
        "v1/unitProductive/list",
        objQuery
      );

      const { data, status } = response;

      if (status === 200) {
        setTableDataState(data.data);
        setLimitState(currentRowsPerPage);
      }
    } catch (err) {
      setTableDataState([]);
    }
  };

  // Función para obtener la lista de clientes
  const getClientsAsync = async () => {
    setLoadingDataState(true);
    try {
      const response = await axiosContext.authAxios.post(
        "v1/client/list-clients",
        { page: 1, limit: 2000 }
      );

      const { data, status } = response;

      if (status === 200) {
        setClientDataState(alphabeticalSort(data.data, "label"));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDataState(false);
    }
  };

  // Función para obtener la lista de huertos
  const getOrchardAsync = async () => {
    setLoadingDataState(true);
    try {
      const response = await axiosContext.authAxios.post(
        "v1/orchard/list-orchard-platform",
        { page: 1, limit: 2000 }
      );

      const { data, status } = response;

      if (status === 200) {
        setOrchardFullDataState(data.data);

        const orchardDataFinal = alterArrayGetFields(data.data, [
          "label",
          "value",
        ]);
        const orchardData = map(orchardDataFinal, (orchard) => ({
          label: orchard.label,
          value: orchard.value,
          id: orchard.value,
        }));
        setOrchardDataState(alphabeticalSort(orchardData, "label"));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDataState(false);
    }
  };

  // Efecto para cargar clientes y huertos al montar el componente
  useEffect(() => {
    getClientsAsync();
    getOrchardAsync();
    queryData();
  }, []);

  // Efecto para filtrar huertos según el cliente seleccionado
  useEffect(() => {
    if (useClient) {
      const resultClient = clientDataState.find(
        (ele) => ele.id === useClient.id
      );

      const filteredOrchardData = orchardFullDataState.filter(
        (ele) => ele.clientValue === resultClient.value
      );

      const orchardDataFinal = alterArrayGetFields(filteredOrchardData, [
        "label",
        "value",
      ]);
      const orchardData = map(orchardDataFinal, (orchard) => ({
        label: orchard.label,
        value: orchard.value,
        id: orchard.value,
      }));
      setOrchardDataState(alphabeticalSort(orchardData, "label"));

      setValue("orchardValue", null);
    } else {
      setOrchardDataState([]);
      setValue("orchardValue", null);
    }
  }, [useClient]);

  const subHeaderComponentMemo = (
    <>
      <SearchBasicTable
        resetFunc={() => {
          setFormQuarterState("");
          debouncedSearch("");
        }}
        id="quarter"
        valuetext={formQuarterState}
        placeholder="Cuartel"
        filterFunc={() => {}}
        onFilter={(value) => handleSearchChange(value)}
        hasIcon={false}
      />
      <hr className={classes.divider} orientation="vertical" />
      <form
        autoComplete="on"
        onSubmit={handleSubmit(onSubmit)}
        className="d-flex flex-row justify-content-center align-items-center"
      >
        <SelectMultipleWidgetFilter
          errors={errors.client}
          loading={loadingDataState}
          control={control}
          labelText="Clientes"
          name="clientValue"
          placeholder="Seleccionar Clientes"
          display="vertical"
          variant="outlined"
          minheight={false}
          isClearable={true}
          isMulti={false}
          isSearchable
          onlyOptionValue={false}
          defaultValue=""
          options={clientDataState}
        />
        <div className="m-md-2">
          <SelectMultipleWidgetFilter
            errors={errors.orchard}
            loading={loadingDataState}
            control={control}
            labelText="Huertos"
            name="orchardValue"
            placeholder="Seleccionar Huertos"
            display="vertical"
            variant="outlined"
            minheight={false}
            isClearable={true}
            isMulti={false}
            isSearchable
            onlyOptionValue={false}
            defaultValue=""
            options={orchardDataState}
          />
        </div>
        <LoadingButton
          variant="contained"
          type="submit"
          color="primary"
          aria-label="filter"
          size="large"
          loading={false}
        >
          <ArrowForwardIosIcon />
        </LoadingButton>
      </form>
    </>
  );

  const columnsConfig = useMemo(
    () => [
      {
        name: "Cliente",
        selector: (row) => row.client,
        sortField: "client",
        sortable: true,
        id: 1,
      },
      {
        name: "ID UNIDAD",
        selector: (row) => row.idUnitProductive,
        sortField: "idUnitProductive",
        sortable: true,
        id: 2,
      },
      {
        name: "Huerto",
        selector: (row) => row.orchard,
        sortField: "orchard",
        sortable: true,
        id: 3,
      },
      {
        name: "Agricola",
        selector: (row) => row.agroBusiness,
        sortField: "agroBusiness",
        sortable: false,
        id: 4,
      },
      {
        name: "Centro de Costo",
        selector: (row) => row.cc,
        sortField: "cc",
        sortable: false,
        id: 5,
      },
      {
        name: "Cuartel",
        selector: (row) => row.quarter,
        sortField: "quarter",
        sortable: true,
        id: 6,
      },
      {
        name: "Especie",
        selector: (row) => row.specie,
        sortField: "specie",
        sortable: false,
        id: 7,
      },
      {
        name: "Variedad",
        selector: (row) => row.variety,
        sortField: "variety",
        sortable: false,
        id: 8,
      },
      {
        name: "Porta Injerto",
        selector: (row) => row.rootStock,
        sortField: "rootStock",
        sortable: false,
        id: 9,
      },
      {
        name: "Status Unidad",
        selector: (row) => row.statusUnitActive,
        cell: (row) => (
          <LabelChip label={row.statusUnitActive ? "Activo" : "Inactivo"} />
        ),
        sortField: "statusUnitActive",
        sortable: false,
        id: 10,
      },
      {
        name: "Status Polinizantes",
        selector: (row) => row.statusPollinator,
        cell: (row) => (
          <LabelChip label={row.statusPollinator ? "Activo" : "Inactivo"} />
        ),
        sortField: "statusPollinator",
        sortable: false,
        id: 11,
      },
    ],
    [classes.chipActive]
  );

  const actionsConfig = [
    {
      name: "Acciones",
      width: "200px",
      id: 12,
      cell: (row) => (
        <div className={classes.actionColumn}>
          <IconActionTableEdit
            handleActionEdit={() => {
              setRowToEditState(row);
              setEditionState(true);
            }}
          />
          <IconActionTableState
            tooltip="Activar/Desactivar Años"
            moduleName="productiveUnit"
            rowState={row.statusUnitActive}
            handleActionState={() => DialogStateHandler(row)}
          />
          <IconActionTableState
            tooltip="Activar/Desactivar Polinizantes"
            rowState={row.statusPollinator}
            handleActionState={() => DialogStateHandlerPollinator(row)}
          />
        </div>
      ),
    },
  ];
  const actions = concatObjectConfigDatatable(actionsConfig[0]);
  const columns = columnsConfig.concat(actions);

  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            {editionState && (
              <EditionAreaWidget
                component={
                  <PuEdit
                    rowEdit={rowToEditState}
                    closeFunc={closeEditionAreaHandler}
                    editFunc={editActionHandler}
                  />
                }
                visible={editionState}
              />
            )}
            <Card elevation={0}>
              <CardHeaderWidget
                title={viewsTexts.titles.productiveUnit.list.title}
                subheader={<BreadCrumbs subTitle="" />}
              />
              <CardContent>
                <div
                  style={{
                    minHeight: "300px",
                  }}
                >
                  <DataTableWidget
                    totalRows={countState}
                    handleOnSort={handleOnSort}
                    fixedHeader={true}
                    fixedHeaderScrollHeight={"590px"}
                    changePagination={handleChangePagination}
                    rowsChange={handleChangeRows}
                    columns={columns}
                    sortField="1"
                    filteredItems={tableDataState}
                    subHeaderComponentMemo={subHeaderComponentMemo}
                    loading={loadingDataTableState}
                    loadingCount={loadingDataTableState}
                    customStyles={customStyles}
                    moduleName="Usuarios"
                    resetPaginationToggle={resetPagination}
                  />
                </div>
                <DialogState
                  openDialog={openDialogStateState}
                  rowState={rowToHandleState}
                  actionState={stateActionDialogHandler}
                  moduleName="productiveUnit"
                  variant="success"
                  openState={setOpenDialogStateState}
                  loading={loadingStateState}
                />
                {/* DIALOG STATE PARA POLLINATOR */}
                <DialogState
                  openDialog={openDialogPollinatorState}
                  rowState={rowToHandleStatePollinator}
                  actionState={stateActionDialogHandlerPollinator}
                  moduleName="statusPollinator"
                  variant="success"
                  openState={setOpenDialogPollinatorState}
                  loading={loadingStatePollinator}
                />
                {successStateState && (
                  <Snackbar
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    open={successStateState}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackBarsComponent}
                  >
                    <SnackBarsComponent
                      variant="success"
                      message={snackBarMessage}
                      handleClose={handleCloseSnackBarsComponent}
                    />
                  </Snackbar>
                )}
              </CardContent>
            </Card>
          </BsCol>
        </BsRow>
      </BsContainer>
    </div>
  );
};

export default PuList;
